/**
 * Renders exactly the items a share link returned — nothing else is fetched or
 * shown. Presentational, so it can be tested without a network.
 */

import type { SharedItem } from '../../store/api/sharingApi';
import { ResultProvenance } from '../portfolio/ResultProvenance';

function formatDuration(seconds: number | null): string | null {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function SharedItemsView({ items, expiresAt }: { items: SharedItem[]; expiresAt: string | null }) {
  const header = items.find((i): i is Extract<SharedItem, { kind: 'profile_header' }> => i.kind === 'profile_header');
  const rest = items.filter((i) => i.kind !== 'profile_header');

  return (
    <div className="space-y-6" data-testid="shared-items">
      {header && (
        <div>
          {header.displayName && <h1 className="text-2xl font-bold text-gray-900">{header.displayName}</h1>}
          {header.headline && <p className="text-gray-600 mt-1">{header.headline}</p>}
        </div>
      )}

      {rest.length === 0 && !header && (
        <p className="text-gray-500" data-testid="shared-items-empty">
          Nothing in this link is available any more.
        </p>
      )}

      <ul className="space-y-4">
        {rest.map((item) => {
          switch (item.kind) {
            case 'highlight':
              return (
                <li key={`h-${item.id}`} className="card" data-kind="highlight">
                  <div className="card-body">
                    <h2 className="font-semibold text-gray-900">{item.title}</h2>
                    {item.description && <p className="text-sm text-gray-600 mt-1">{item.description}</p>}
                    {formatDuration(item.durationSeconds) && (
                      <p className="text-xs text-gray-400 mt-1">{formatDuration(item.durationSeconds)}</p>
                    )}
                    {item.result && <ResultProvenance result={item.result} testId="shared-result-provenance" />}
                    {item.transcriptExcerpt && (
                      <blockquote className="mt-3 text-sm text-gray-700 border-l-4 border-gray-200 pl-3 italic">
                        {item.transcriptExcerpt}
                      </blockquote>
                    )}
                  </div>
                </li>
              );
            case 'credential':
              return (
                <li key={`c-${item.id}`} className="card" data-kind="credential">
                  <div className="card-body">
                    <h2 className="font-semibold text-gray-900">{item.domainLabel}</h2>
                    <p className="text-sm text-gray-600">
                      Verified credential · issued {new Date(item.issuedAt).toLocaleDateString()}
                    </p>
                    <a className="text-sm text-primary-600 hover:underline" href={`/verify/${item.verificationHash}`}>
                      Verify this credential
                    </a>
                  </div>
                </li>
              );
            case 'authority_summary':
              return (
                <li key="authority" className="card" data-kind="authority_summary">
                  <div className="card-body">
                    <h2 className="font-semibold text-gray-900">Authority</h2>
                    <p className="text-sm text-gray-600">
                      <span className="capitalize">{item.tier}</span> tier · score {item.score} · {item.totalSessions} sessions
                    </p>
                  </div>
                </li>
              );
            default:
              return null;
          }
        })}
      </ul>

      {expiresAt && (
        <p className="text-xs text-gray-400">This link expires {new Date(expiresAt).toLocaleString()}.</p>
      )}
    </div>
  );
}
